
/* planet-ingresses.js
 * Ingressi planetari nei segni zodiacali su TradingView Advanced Charts.
 * Mostra: linea verticale + balloon grande (StickedPoint al CLOSE della barra).
 * Parser CSV robusto (BOM; ; , TAB; date dd/mm/yyyy o "dd mon yy|yyyy").
 *
 * NOTE:
 * - Usa Drawings API: createMultipointShape('vertical_line') + createShape('balloon').
 * - Punto balloon: StickedPoint { time, price: 'close' } → agganciato al CLOSE della barra.
 * - Gestione entity IDs con Set, render/clear serializzati tramite ref.lock (Promise chain).
 */

/* =========================
   CONFIG
   ========================= */


import { getEphemeridesRows } from './ephemerides-provider.js';

const CSV_URL = '/assets/effemeridi.csv'; // <-- aggiorna path

const PLANET_COLUMNS = {
  SOLE: 'Sole',
  LUNA: 'Luna',
  MERCURIO: 'Mercurio',
  VENERE: 'Venere',
  MARTE: 'Marte',
  GIOVE: 'Giove',
  SATURNO: 'Saturno',
  URANO: 'Urano',
  NETTUNO: 'Nettuno',
};

// Unicode "ufficiali" per i pianeti
const PLANET_ICON = {
  SOLE: '☉',
  LUNA: '☽',
  MERCURIO: '☿',
  VENERE: '♀',
  MARTE: '♂',
  GIOVE: '♃',
  SATURNO: '♄',
  URANO: '♅',
  NETTUNO: '♆',
};


// Unicode dei segni (0..11)
const SIGN_ICON = ['♈','♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓'];

// Palette per pianeta (personalizzabile)
const PLANET_COLOR = {
  SOLE:     '#E5A00D',
  LUNA:     '#9FA8DA',
  MERCURIO: '#8E8E8E',
  VENERE:   '#C95F9E',
  MARTE:    '#D9534F',
  GIOVE:    '#4CAF50',
  SATURNO:  '#7D6E57',
  URANO:    '#00BCD4',
  NETTUNO:  '#3F51B5',
};



// Nomi IT dei segni (0..11)
const SIGN_NAME = [
  'Ariete','Toro','Gemelli','Cancro','Leone','Vergine',
  'Bilancia','Scorpione','Sagittario','Capricorno','Aquario','Pesci'
];

// Friendly date "dd MMM yyyy" (UTC) con mesi IT
const MONTH_IT = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];



/* =========================
   UTILS
   ========================= */
const mod360 = d => ((d % 360) + 360) % 360;
const signIdx = d => Math.floor(mod360(d) / 30);
const itMonth = { gen:0,feb:1,mar:2,apr:3,mag:4,giu:5,lug:6,ago:7,set:8,ott:9,nov:10,dic:11 };

// parse date: supporta "dd mon yy|yyyy" e "dd/mm/yy|yyyy"
function parseItDate(s) {
  const str = s.trim().toLowerCase();

  // "dd mon yy|yyyy"
  const mIt = str.match(/^(\d{1,2})\s+([a-z]{3})\s+(\d{2}|\d{4})$/i);
  if (mIt) {
    const dd = parseInt(mIt[1], 10);
    const mon = itMonth[mIt[2]];
    let yy = parseInt(mIt[3], 10);
    if (yy < 100) yy += (yy >= 70 ? 1900 : 2000);
    if (mon == null) throw new Error('Mese non riconosciuto: ' + mIt[2]);
    return Math.floor(Date.UTC(yy, mon, dd) / 1000);
  }

  // "dd/mm/yy|yyyy"
  const mNum = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (mNum) {
    const dd = parseInt(mNum[1], 10);
    const mm = parseInt(mNum[2], 10) - 1;
    let yy = parseInt(mNum[3], 10);
    if (yy < 100) yy += (yy >= 70 ? 1900 : 2000);
    if (mm < 0 || mm > 11) throw new Error('Mese fuori range: ' + (mm+1));
    if (dd < 1 || dd > 31) throw new Error('Giorno fuori range: ' + dd);
    return Math.floor(Date.UTC(yy, mm, dd) / 1000);
  }

  throw new Error('Data non riconosciuta: ' + s);
}

// CSV robusto: BOM, delimitatori (; , TAB), header case-insensitive, fallback riconoscimento "Data", decimali con ','
export function parseEphemeridesCSV(csvText) {
  // rimuovi BOM
  let cleaned = csvText.replace(/^\uFEFF/, '');
  const peek = cleaned.slice(0, 200).toLowerCase();

  // blocca HTML al posto del CSV
  if (peek.includes('<html') || peek.includes('<!doctype') || peek.includes('<head>')) {
    throw new Error('Il contenuto scaricato sembra HTML, non CSV. Verifica CSV_URL e la cartella statica.');
  }

  const lines = cleaned.split(/\r?\n/).filter(line => line.trim().length);
  if (!lines.length) throw new Error('File CSV vuoto o non leggibile.');

  // autodetect delimitatore
  const candidates = [';', ',', '\t'];
  const sample = lines.slice(0, Math.min(lines.length, 5));
  const score = Object.fromEntries(candidates.map(d => [d, 0]));
  for (const l of sample) for (const d of candidates) score[d] += (l.split(d).length - 1);
  const delimiter = candidates.sort((a, b) => score[b] - score[a])[0];
  if (score[delimiter] === 0) throw new Error('Impossibile rilevare il delimitatore (né ; né , né \\t).');

  // trova header: prima riga con almeno 2 colonne
  const headerLineIndex = lines.findIndex(l => l.split(delimiter).length >= 2);
  if (headerLineIndex < 0) throw new Error(`Header non trovato: nessuna riga con separatore "${delimiter}"`);

  const headerCells = lines[headerLineIndex]
    .split(delimiter)
    .map(c => c.replace(/^\uFEFF/, '').trim());

  // mappa case-insensitive
  const headerLc = headerCells.map(c => c.toLowerCase());
  const idx = Object.fromEntries(headerLc.map((h, i) => [h, i]));

  // trova colonna "Data" (o deducila dalla riga successiva)
  let dataCol = idx['data'];
  if (dataCol == null) {
    for (let j = 0; j < headerCells.length && dataCol == null; j++) {
      const cell = (lines[headerLineIndex + 1] || '').split(delimiter)[j] || '';
      if (/^\s*\d{1,2}\s+[A-Za-z]{3}\s+\d{2,4}\s*$/.test(cell) || /^\s*\d{1,2}\/\d{1,2}\/\d{2,4}\s*$/.test(cell)) {
        dataCol = j;
      }
    }
  }
  if (dataCol == null) throw new Error(`Colonna "Data" non trovata. Header: [${headerCells.join(', ')}]`);

  // righe dati
  const out = [];
  for (let i = headerLineIndex + 1; i < lines.length; i++) {
    const cols = lines[i].split(delimiter);
    if (cols.length < headerCells.length) continue;

    const timeStr = cols[dataCol];
    let time;
    try { time = parseItDate(timeStr); } catch { continue; }

    const values = {};
    for (const key in PLANET_COLUMNS) {
      const nameLc = PLANET_COLUMNS[key].toLowerCase();
      const j = idx[nameLc];
      if (j == null) continue;
      const val = (cols[j] || '').replace(',', '.').trim();
      const deg = Number.parseFloat(val);
      if (Number.isFinite(deg)) values[key] = deg;
    }

    out.push({ time, values });
  }

  if (!out.length) throw new Error('Nessuna riga dati valida trovata nel CSV.');
  out.sort((a, b) => a.time - b.time);
  return out;
}

// delta continuo in (-180, 180]
function deltaContinuous(a, b) {
  let d = b - a;
  if (d > 180) d -= 360;
  if (d <= -180) d += 360;
  return d;
}

// restituisce al massimo 1 confine (multiplo di 30°) attraversato tra a->b
function nextBoundaryBetween(prevDeg, currDeg) {
  const p = mod360(prevDeg), c = mod360(currDeg);
  const delta = deltaContinuous(p, c);
  const dir = Math.sign(delta);
  if (dir === 0) return null;

  const step = 30 * dir;
  let b = dir > 0 ? Math.ceil(p / 30) * 30 : Math.floor(p / 30) * 30;
  if (b === p) b += step;

  const target = p + delta; // destinazione locale
  if ((dir > 0 && b <= target) || (dir < 0 && b >= target)) return b;
  return null;
}

// interpolazione lineare dell'istante di crossing
function interpolateTime(aTime, aDeg, bTime, bDeg, boundary) {
  const p = mod360(aDeg);
  const delta = deltaContinuous(p, mod360(bDeg));
  const denom = (p + delta) - p;
  const frac = denom === 0 ? 0 : (boundary - p) / denom;
  const f = Math.max(0, Math.min(1, frac));
  return Math.round(aTime + f * (bTime - aTime));
}

function formatDayUTC(dayBucketSec) {
  const d = new Date(dayBucketSec * 1000);
  const dd = String(d.getUTCDate()).padStart(2,'0');
  const mon = MONTH_IT[d.getUTCMonth()];
  const yyyy = d.getUTCFullYear();
  // es: "18 Mag 2026"
  return `${dd} ${mon} ${yyyy}`;
}



function formatIngressLine(ev) {
  if (!ev || !ev.planet || typeof ev.signIdx !== 'number') {
    return 'Dato non disponibile';
  }
  const planetKey = String(ev.planet);
  const signIdx   = Number(ev.signIdx);
  const planetName = PLANET_COLUMNS?.[planetKey] ?? planetKey;
  const signName   = SIGN_NAME?.[signIdx] ?? `Segno ${signIdx}`;
  return `Ingresso: ${planetName} in ${signName}`;
}




/* =========================
   INGRESSI NEI SEGNI
   ========================= */
function computeIngressEventsForPlanet(rows, planetKey, from = -Infinity, to = Infinity) {
  const events = [];
  for (let i = 1; i < rows.length; i++) {
    const a = rows[i - 1], b = rows[i];
    const aDeg = a.values[planetKey], bDeg = b.values[planetKey];
    if (!Number.isFinite(aDeg) || !Number.isFinite(bDeg)) continue;

    const sA = signIdx(aDeg), sB = signIdx(bDeg);
    if (sA !== sB) {
      const boundary = nextBoundaryBetween(aDeg, bDeg);
      if (boundary != null) {
        const tCross = interpolateTime(a.time, aDeg, b.time, bDeg, boundary);
        if (tCross >= from && tCross <= to) {
          events.push({ time: tCross, planet: planetKey, signIdx: signIdx(boundary) });
        }
      }
    }
  }
  return events.sort((x, y) => x.time - y.time);
}


// Raggruppa eventi per giorno (UTC): tutti gli ingressi nello stesso giorno finiscono nello stesso gruppo
function groupEventsByDay(events) {
  const m = new Map();
  for (const ev of events) {
    // Calcola il timestamp di mezzanotte UTC per il giorno dell'evento
    const dayBucket = Math.floor(ev.time / 86400) * 86400; // 86400 sec = 1 giorno
    if (!m.has(dayBucket)) m.set(dayBucket, []);
    m.get(dayBucket).push(ev);
  }
  return m;
}


// Calcola le Y di uno "stack" verticale vicino al top del pane
function computeStackYPositions(chart, count, padTopPct = 0.10, deltaPct = 0.06) {
  // const pr = chart.getVisiblePriceRange(); // { from, to }  deprecated
  let pr;
  if (typeof chart.priceScale === 'function') {
    // Nuove versioni: Price Scale API
    pr = chart.priceScale().getVisiblePriceRange();
  } else {
    // Fallback per versioni precedenti
    pr = chart.getVisiblePriceRange();
  }
  const H  = pr.to - pr.from;
  const padTop = H * padTopPct;
  const delta  = H * deltaPct;
  const base   = pr.to - padTop;

  // centro lo stack rispetto a 'base'
  const ys = [];
  const start = base - (delta * (count - 1) / 2);
  for (let i = 0; i < count; i++) ys.push(start + i * delta);
  return ys;
}




/* =========================
   STORE & SERIALIZZAZIONE
   ========================= */
function ensureIdsSet(ref) {
  if (!(ref.ids instanceof Set)) {
    if (Array.isArray(ref.ids)) ref.ids = new Set(ref.ids);
    else if (ref.ids != null) ref.ids = new Set([ref.ids]);
    else ref.ids = new Set();
  }
}

/* =========================
   DISEGNO (LINEA + BALLOON)
   ========================= */


// Crea una vertical_line in modo sicuro (con guardie e catch)
async function safeCreateVerticalLine(chart, t, options) {
  // 1) time valido?
  const time = Math.floor(Number(t));
  if (!Number.isFinite(time)) {
    console.warn('[Ingressi] safeCreateVerticalLine: time non valido, skip:', t);
    return null;
  }

  // 2) range visibile valido?
  const range = chart.getVisibleRange?.();
  const from = range?.from, to = range?.to;
  if (!Number.isFinite(from) || !Number.isFinite(to)) {
    console.warn('[Ingressi] safeCreateVerticalLine: visible range non valido, skip:', range);
    return null;
  }

  // 3) clamp opzionale del time dentro il range (evita out-of-bounds in transizioni)
  const clamped = Math.min(Math.max(time, from), to);

  // 4) tentativo di creazione con catch
  try {
    const id = await chart.createMultipointShape(
      [{ time: clamped }], // TimePoint
      options
    );
    return id;
  } catch (err) {
    console.warn('[Ingressi] createMultipointShape vertical_line FAILED:', { time: clamped, options, err });
    return null;
  }
}



// Balloon grande: StickedPoint agganciato al CLOSE della barra dell'ingresso
async function drawIngressBalloon(chart, ev, ref) {
  ensureIdsSet(ref);

  const text  = `${PLANET_ICON[ev.planet] || ev.planet}  ${SIGN_ICON[ev.signIdx]}`;
  const color = PLANET_COLOR[ev.planet] || '#888';

  const id = await Promise.resolve(
    chart.createShape(
      { time: ev.time, price: 'close' }, // StickedPoint → punto determinato dal CLOSE della barra
      {
        shape: 'balloon',
        text,
        lock: true,
        disableSelection: true,    // <<< impedisce la selezione e il toolbox
        disableSave: true,         // facoltativo: non salvare nel layout
        showInObjectsTree: false,  // facoltativo: non mostrare nell’Object Tree
        overrides: {
          text,
          //font: 'Segoe UI Symbol, "Noto Emoji", sans-serif',
          font: 'NotoSansSymbols2, Segoe UI Symbol, sans-serif', // usa il font caricato
          fontsize: 22,
          bold: false,
          color: color,            // <<< colore del testo del balloon
          fillBackground: true,
          backgroundColor: '#C5DCFA',
          backgroundTransparency: 60,
          drawBorder: true,
          borderColor: color,          // <<< bordo del balloon del colore del pianeta
          fixedSize: true
        }
      }
    )
  );

  ref.ids.add(id);
  return id;
}


// Disegna un gruppo con collisione: UNA linea verticale + N etichette con offset Y



// Disegna 1 sola 'vertical_line' + N etichette con Y esplicite (anti-overlap)

// Disegna un gruppo con collisione: UNA linea verticale + N etichette 'text' con offset Y
// Versione "A": niente balloon in collisione (pulito e leggibile)
async function drawCollisionStack(chart, groupEvents, ref) {
  const time = groupEvents[0].time;
  
  // Prima di creare la linea verticale, verifica che ev.time sia un numero valido:
  if (!Number.isFinite(time)) {
    console.warn('[Ingressi] Collisione con time non valido, skip:', groupEvents);
    return;
  }

  const dayBucket = Math.floor(time / 86400) * 86400;
  
  // Ordine deterministico opzionale
  if (typeof PLANET_KEYS !== 'undefined') {
    groupEvents.sort((a, b) => PLANET_KEYS.indexOf(a.planet) - PLANET_KEYS.indexOf(b.planet));
  }

  // 1) Una sola vertical line (tinta del primo pianeta)
  const firstColor = PLANET_COLOR[groupEvents[0].planet] ?? '#888';
  const lineText   = `Ingressi multipli (${groupEvents.length})`;
  /*
  const lineId     = await chart.createMultipointShape(
    [{ time }],
    {
      shape: 'vertical_line',
      text: lineText,
      lock: true,      
      disableSelection: true,    // <<< impedisce la selezione e il toolbox
      disableSave: true,         // facoltativo: non salvare nel layout
      showInObjectsTree: false,  // facoltativo: non mostrare nell’Object Tree
      overrides: { color: firstColor, linestyle: 0, linewidth: 2 },
      zOrder: 'top'
    }
  );
  */
  const lineId = await safeCreateVerticalLine(chart, time, {
    shape: 'vertical_line',
    text: `Ingressi multipli (${groupEvents.length})`,
    lock: true,
    disableSelection: true,
    disableSave: true,
    showInObjectsTree: false,
    overrides: { color: firstColor, linestyle: 0, linewidth: 2 },
    zOrder: 'top'
  });
  if (!lineId) return; // se fallisce, esci: eviti il resto dello stack
  ensureIdsSet(ref);
  ref.ids.add(lineId);
  ref.idToDayBucket.set(lineId, dayBucket);      // <<< NEW

  // 2) Y dello stack: distanza e centratura vicino al top del pane
  const ys = computeStackYPositions(
    chart,
    groupEvents.length,
    ref.collision?.padTopPct ?? 0.10,
    ref.collision?.deltaPct   ?? 0.10   // passo verticale (10% del range visibile di default)
  );

  // (Opzionale) se vuoi balloon sfalsati in futuro:
  const pr = chart.getVisiblePriceRange();
  const H  = pr.to - pr.from;
  const balloonOffset = (ref.collision?.balloonOffsetPct ?? 0.5) * (ref.collision?.deltaPct ?? 0.10) * H;

  // 3) Etichette grandi su due righe (SEGNO sopra, PIANETA sotto), shape: 'text'
  for (let i = 0; i < groupEvents.length; i++) {
    const ev    = groupEvents[i];
    const color = PLANET_COLOR[ev.planet] ?? '#888';

    // Etichetta posizionata con PricedPoint (anti-overlap)
    const id = await drawAnchoredLabel(chart, ev, ref, {
      y: ys[i],
      color
      // NOTE: shape 'text' non usa horzLabelsAlign; togliamo align per chiarezza
    });
    ref.ids.add(id);
    ref.idToDayBucket.set(id, dayBucket);

    // 4) balloon in collisione - creavano sovrapposizione/confusione
    // Se un giorno li vuoi riattivare, scommenta il blocco seguente
    //
    if (ref.displayMode !== 'line' && ref.collision?.showBalloon !== false) {
      const balId = await chart.createShape(
        { time, price: ys[i] + balloonOffset },
        {
          shape: 'balloon',
          text: `${PLANET_ICON[ev.planet] ?? ev.planet} ${SIGN_ICON[ev.signIdx]}`,
          lock: true,
          disableSelection: true,    // <<< impedisce la selezione e il toolbox
          disableSave: true,         // facoltativo: non salvare nel layout
          showInObjectsTree: false,  // facoltativo: non mostrare nell’Object Tree
          overrides: {
            text: `${PLANET_ICON[ev.planet] ?? ev.planet} ${SIGN_ICON[ev.signIdx]}`,
            font: 'NotoSansSymbols2, Segoe UI Symbol, sans-serif',
            fontsize: 16,
            color: '#000',
            borderColor: color,
            fillBackground: true,
            backgroundColor: '#f2f2f2',
            transparency: 10,
            fixedSize: true
          },
          zOrder: 'top'
        }
      );
      ref.ids.add(balId);
      ref.idToDayBucket.set(balId, dayBucket);
    }
  }
}



async function drawLargeIconsText(chart, ev, ref, { fs = 28 } = {}) {
  const text = `${SIGN_ICON[ev.signIdx]}\n${PLANET_ICON[ev.planet] || ev.planet}`;
  const color = PLANET_COLOR[ev.planet] || '#888';

  const id = await Promise.resolve(
    chart.createShape(
      { time: ev.time, price: 'close' }, // stessa barra
      {
        shape: 'text',
        text,
        lock: true,
        disableSelection: true,    // <<< impedisce la selezione e il toolbox
        disableSave: true,         // facoltativo: non salvare nel layout
        showInObjectsTree: false,  // facoltativo: non mostrare nell’Object Tree
        overrides: {
          text,
          font: 'NotoSansSymbols2, Segoe UI Symbol, sans-serif',
          fontsize: fs,           // <<< ICONE GRANDI QUI
          bold: false,            // usa il peso del font per coerenza
          color: color,           // colore del pianeta (uniforme)
          fillBackground: false,  // niente box (resta compatto)
          drawBorder: false,
          fixedSize: true         // resta grande a qualsiasi zoom
        }
      }
    )
  );

  ref.ids.add(id);
  return id;
}


// Mini-balloon: bordo color pianeta, sfondo trasparente → dimensione percepita minore
async function drawMiniBalloon(chart, ev, ref, { bg = '#f2f2f2', transparent = false } = {}) {
  const text  = `${PLANET_ICON[ev.planet] || ev.planet}  ${SIGN_ICON[ev.signIdx]}`;
  const color = PLANET_COLOR[ev.planet] || '#888';

  const id = await Promise.resolve(
    chart.createShape(
      { time: ev.time, price: 'close' }, // StickedPoint: agganciato alla barra
      {
        shape: 'balloon',
        text,
        lock: true,
        disableSelection: true,    // <<< impedisce la selezione e il toolbox
        disableSave: true,         // facoltativo: non salvare nel layout
        showInObjectsTree: false,  // facoltativo: non mostrare nell’Object Tree
        overrides: {
          text,
          font: 'NotoSansSymbols2, Segoe UI Symbol, sans-serif', 
          fontsize: 16,            // <<< BALLOON PICCOLO
          color: '#000',           // testo secondario scuro
          borderColor: color,      // bordo tinta pianeta
          fillBackground: !transparent,
          backgroundColor: bg,     // sfondo chiaro
          transparency: transparent ? 100 : 10, // 100=trasparente; 10=quasi opaco
          fixedSize: true
        }
      }
    )
  );

  ref.ids.add(id);
  return id;
}


async function drawIngressCompact(chart, ev, ref) {
  await drawMiniBalloon(chart, ev, ref); // contorno leggero
  await drawLineIconLabel(chart, ev, ref); // icone grandi su 2 righe (SIGN\nPLANET) con color = pianeta
}



// Linea verticale + balloon
async function drawIngresses(chart, ingressEvents, ref) {
  ensureIdsSet(ref);

  // Normalizza: array sempre
  const evs = Array.isArray(ingressEvents) ? ingressEvents : [ingressEvents];

  // Se sono più eventi con stesso time -> delega allo stack anticollisione
  const sameTime = evs.length > 1 && evs.every(e => e.time === evs[0].time);
  if (sameTime) {
    await drawCollisionStack(chart, evs, ref);
    return;
  }

  // Caso normale: uno o più eventi MA su time diversi
  for (const ev of evs) {
    
    // Prima di creare la linea verticale, verifica che ev.time sia un numero valido:
    if (!Number.isFinite(ev.time)) {
      console.warn('[Ingressi] Evento con time non valido, skip:', ev);
      continue;
    }

    // <<< NEW: dayBucket dalla data dell'evento (mezzanotte UTC)
    const dayBucket = Math.floor(ev.time / 86400) * 86400;
    const text  = `${PLANET_ICON[ev.planet] || ev.planet}  →  ${SIGN_ICON[ev.signIdx]}`;
    const color = PLANET_COLOR[ev.planet] || '#888';

    // 1) Linea verticale
    /*
    const lineId = await Promise.resolve(
      chart.createMultipointShape(
        [{ time: ev.time }],
        {
          shape: 'vertical_line',
          text,
          lock: true,
          disableSelection: true,    // <<< impedisce la selezione e il toolbox
          disableSave: true,         // facoltativo: non salvare nel layout
          showInObjectsTree: false,  // facoltativo: non mostrare nell’Object Tree
          overrides: { color, linestyle: 0, linewidth: 2 },
          zOrder: 'top'
        }
      )
    );
    */
   
    const lineId = await safeCreateVerticalLine(chart, ev.time, {
      shape: 'vertical_line',
      text,
      lock: true,
      disableSelection: true,
      disableSave: true,
      showInObjectsTree: false,
      overrides: { color, linestyle: 0, linewidth: 2 },
      zOrder: 'top'
    });

    if (!lineId) {
      // se la creazione è fallita, passa al prossimo evento (evita crash)
      continue;
    }
    ref.ids.add(lineId);
    ref.idToDayBucket.set(lineId, dayBucket);  

    // 2) Etichetta (solo linea) -> grande e compatta (ancorata al close)
    if (ref.displayMode === 'line' && ref.lineLabel?.enabled) {
      const labelId = await drawLineIconLabel(chart, ev, ref); // qui resta al close: non c'è collisione      
      ref.ids.add(labelId);
      ref.idToDayBucket.set(labelId, dayBucket);    
    }

    // 3) Balloon (se previsto)
    if (ref.displayMode !== 'line') {
      const balloonId = await drawMiniBalloon(chart, ev, ref); // deve RESTITUIRE l'id
      ref.ids.add(balloonId);
      ref.idToDayBucket.set(balloonId, dayBucket);
    }
  }
}


// SEGNO: etichetta compatta (sfondo chiaro + bordo), testo NERO
async function drawSignTag(chart, ev, ref, opts = {}) {
  const sign = SIGN_ICON[ev.signIdx];
  const text = `${sign}\n`; // seconda riga vuota per mantenere box compatto
  const bg   = opts.bgColor ?? '#f2f2f2'; // o '#ffffff' per bianco
  const border = opts.borderColor ?? '#d0d0d0';
  const fs   = opts.fontsize ?? (ref.lineLabel?.fontsize ?? 28);

  const id = await Promise.resolve(
    chart.createShape(
      { time: ev.time, price: 'close' }, // StickedPoint → agganciato al CLOSE
      {
        shape: 'text',
        text,
        lock: true,
        disableSelection: true,    // <<< impedisce la selezione e il toolbox
        disableSave: true,         // facoltativo: non salvare nel layout
        showInObjectsTree: false,  // facoltativo: non mostrare nell’Object Tree
        overrides: {
          text,
          //font: 'Segoe UI Symbol, "Noto Emoji", sans-serif',
          font: 'NotoSansSymbols2, Segoe UI Symbol, sans-serif', // usa il font caricato
          fontsize: fs,
          bold: true,
          color: '#000000',          // 🔴 NERO per il segno
          fillBackground: true,      // sfondo attivo
          backgroundColor: bg,       // 🔴 sfondo chiaro
          // in alcune build si usa "transparency", in altre "backgroundTransparency"
          // se vuoi opaco: 0; se vuoi un pelo trasparente: 10–20
          backgroundTransparency: 0,
          drawBorder: true,
          borderColor: border,
          fixedSize: true
        }
      }
    )
  );

  ref.ids.add(id);
  return id;
}



// PIANETA: testo senza sfondo, colore del pianeta, appena sotto al segno
async function drawPlanetLabel(chart, ev, ref, opts = {}) {
  const planetIcon = PLANET_ICON[ev.planet] || ev.planet;
  const color = PLANET_COLOR[ev.planet] || '#888';
  const fs    = opts.fontsize ?? (ref.lineLabel?.fontsize ?? 28);

  // offset verticale minimo: 0.5% del range visibile
  const pr = chart.getVisiblePriceRange();
  const delta = (pr.to - pr.from) * 0.005;

  const id = await Promise.resolve(
    chart.createShape(
      { time: ev.time, price: 'close' - delta }, // pochi pixel sotto
      {
        shape: 'text',
        text: planetIcon,
        lock: true,
        disableSelection: true,    // <<< impedisce la selezione e il toolbox
        disableSave: true,         // facoltativo: non salvare nel layout
        showInObjectsTree: false,  // facoltativo: non mostrare nell’Object Tree
        overrides: {
          text: planetIcon,
          //font: 'Segoe UI Symbol, "Noto Emoji", sans-serif',
          font: 'NotoSansSymbols2, Segoe UI Symbol, sans-serif', // usa il font caricato
          fontsize: fs,
          bold: true,
          color: color,             // 🔴 colore del pianeta
          fillBackground: false,    // niente sfondo
          drawBorder: false,
          fixedSize: true
        }
      }
    )
  );

  ref.ids.add(id);
  return id;
}



// Etichetta grande su 2 righe (SEGNO sopra, PIANETA sotto).
// Se opts.y è passato, usa PricedPoint { time, price: y } per posizionamento anticollisione.
// Altrimenti usa StickedPoint { time, price:'close' }.
async function drawLineIconLabel(chart, ev, ref, opts = {}) {
  const text  = `${SIGN_ICON[ev.signIdx]}\n${PLANET_ICON[ev.planet] || ev.planet}`;
  const color = (opts.color ?? PLANET_COLOR[ev.planet] ?? '#888');
  const fs    = (opts.fontsize ?? (ref.lineLabel?.fontsize ?? 28));

  const point = (typeof opts.y === 'number')
    ? { time: ev.time, price: opts.y }     // PricedPoint (Y esplicita)
    : { time: ev.time, price: 'close' };   // StickedPoint (close)

  const id = await Promise.resolve(
    chart.createShape(
      point,
      {
        shape: 'text',
        text,
        lock: true,
        disableSelection: true,    // <<< impedisce la selezione e il toolbox
        disableSave: true,         // facoltativo: non salvare nel layout
        showInObjectsTree: false,  // facoltativo: non mostrare nell’Object Tree
        overrides: {
          text,
          font: 'NotoSansSymbols2, Segoe UI Symbol, sans-serif',
          fontsize: fs,
          bold: false,
          color: color,        // <- chiave corretta per il testo
          fillBackground: false,
          drawBorder: true,
          fixedSize: true
        },
        zOrder: 'top'
      }
    )
  );

  if (typeof opts.y === 'number') console.debug('[Anti-collisione] drawLineIconLabel con Y=', opts.y, ev);
  
  // questa helper AGGIUNGE direttamente l'id allo store (coerente col tuo uso attuale)
  ensureIdsSet(ref);
  ref.ids.add(id);
  return id;
}



async function drawAnchoredLabel(chart, ev, ref, opts = {}) {
  const text = `${SIGN_ICON[ev.signIdx]}\n${PLANET_ICON[ev.planet] ?? ev.planet}`;
  const color = opts.color ?? PLANET_COLOR[ev.planet] ?? '#888';
  const fs = opts.fontsize ?? (ref.lineLabel?.fontsize ?? 28);
  const y = Number(opts.y);
  const id = await chart.createShape(
    { time: ev.time, price: y },          // PricedPoint (Y esplicita)
      {
        shape: 'text',
        text,
        lock: true,
        disableSelection: true,    // <<< impedisce la selezione e il toolbox
        disableSave: true,         // facoltativo: non salvare nel layout
        showInObjectsTree: false,  // facoltativo: non mostrare nell’Object Tree
        overrides: {
          text,
          font: 'NotoSansSymbols2, Segoe UI Symbol, sans-serif',
          fontsize: fs,
          bold: false,
          color,
          fillBackground: false,
          drawBorder: false,
          fixedSize: true,
          wordWrap: false
        },
        zOrder: 'top'
      }
    );
    ensureIdsSet(ref);
    ref.ids.add(id);
    return id;
 }



/* =========================
   CLEAR SAFE
   ========================= */

async function clearIngressDrawings(chart, ref) {
  ensureIdsSet(ref);
  if (ref.ids.size === 0) return;

  const toRemove = await Promise.all([...ref.ids].map(id => Promise.resolve(id)));

  for (const id of toRemove) {
    try { 
      chart.removeEntity(id); 
      // rimuovi anche dalla mappa id->giorno
      if (ref.idToDayBucket) ref.idToDayBucket.delete(id);
    } catch (e) { 
      /* se non esiste più: ok */ 
    }
  }
  ref.ids.clear();
}


/* =========================
   CONTROLLER
   ========================= */

export async function initPlanetIngresses(widget, options = {}) {

  const {
    csvUrl = options.csvUrl,
    enabledPlanets: enabledPlanetsOpt = Object.keys(PLANET_COLUMNS),
    //displayMode: displayModeOpt = 'line+balloon'   // <<< NEW: default
    displayMode: displayModeOpt = 'line'   // <<< NEW: default
  } = options;

  const chart = widget.activeChart();

  // carica e parse CSV
  /*
  const csvText = await fetch(csvUrl).then(r => r.text());
  const rows = parseEphemeridesCSV(csvText);
  */
 
  const rows = await getEphemeridesRows(csvUrl); // utilizza ephemerides-provider per eseguire una sola volta il parsing e poi condividere le rows

  // stato interno
  const ref = {
    ids: new Set(),         // evita duplicati
    sub: null,
    active: false,
    lock: Promise.resolve(), // serializza render/clear
    planets: new Set(enabledPlanetsOpt), // <<< ORA i pianeti sono nello stato
    collision: {
      padTopPct: 0.10,  // distanza dal top del pane (10%)
      deltaPct:  0.10,  // distanza verticale tra etichette (10%)  <<< più ampia di prima
      align:     'center', // 'left' | 'center' | 'right' (per anchored_text)      
      showBalloon: false,     // <-- disattiva balloon in collisione (consigliato)
      balloonOffsetPct: 0.5,  // se li tieni: sposta di mezzo delta
    },
    displayMode: displayModeOpt,                     // <<< NEW: nello stato
    lineLabel: { enabled: true, fontsize: 28 }, // <<< NEW: etichetta icone ingrandite in “Solo linea”    
    idToDayBucket: new Map(),   // <<< NEW: mappa EntityId -> dayBucket (mezzanotte UTC del giorno)
    tooltip: { el: null, dayIndex: new Map(), enabled: true }
  };


  // -- INIZIO tooltip --


  // Tooltip state
  ref.tooltip = {
    el: null,
    dayIndex: new Map(),
    enabled: true,
    clickedDrawingLast: false, // se il click ha colpito un drawing
    hideTimer: null,           // timer di chiusura "differita"
  };


  // Risolve il container del widget; puoi passarlo anche via options.tooltipContainer
  function resolveWidgetContainer(widget, options) {
    if (options && options.tooltipContainer instanceof HTMLElement) return options.tooltipContainer;
    if (widget && widget._id) {
      const el = document.getElementById(widget._id);
      if (el) return el;
    }
    // Fallback ragionevole (adatta se la tua integrazione usa container diversi)
    return document.querySelector('.tv-chart-view')
        || document.querySelector('.chart-container')
        || document.body;
  }


  // Tema-aware: colore invertito rispetto al pane
  function computeTooltipTheme(parent) {
    const bg = window.getComputedStyle(parent).backgroundColor || '#ffffff';
    const m = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    const [r,g,b] = m ? [parseInt(m[1],10), parseInt(m[2],10), parseInt(m[3],10)] : [255,255,255];
    const Y = 0.2126*r + 0.7152*g + 0.0722*b; // luminanza percettiva
    const isDark = Y < 140;
    return {
      bg:   isDark ? '#ffffff' : '#1f222d',
      text: isDark ? '#000000' : '#ffffff',
      border: isDark ? '#d0d0d0' : '#3b3f4a',
      shadow: isDark ? '0 8px 24px rgba(0,0,0,0.25)' : '0 8px 24px rgba(0,0,0,0.35)'
    };
  }



  // Tema inverso in base al colore di sfondo del body (semplice & robusto)
  function computeGlobalTooltipTheme() {
    const bg = window.getComputedStyle(document.body).backgroundColor || '#ffffff';
    const m = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    const [r,g,b] = m ? [parseInt(m[1],10), parseInt(m[2],10), parseInt(m[3],10)] : [255,255,255];
    const Y = 0.2126*r + 0.7152*g + 0.0722*b; // luminanza
    const isDark = Y < 140;
    return {
      bg:   isDark ? '#ffffff' : '#1f222d',
      text: isDark ? '#000000' : '#ffffff',
      border: isDark ? '#d0d0d0' : '#3b3f4a',
      shadow: isDark ? '0 10px 28px rgba(0,0,0,0.25)' : '0 10px 28px rgba(0,0,0,0.35)'
    };
  }

  // Crea overlay HTML
  function ensureTooltipElement() {
    if (ref.tooltip.el) return ref.tooltip.el;

    const theme = computeGlobalTooltipTheme();
    const tip = document.createElement('div');

    // 💪 Overlay globale, non clippato dal chart
    tip.style.position = 'fixed';              // << viewport coords (clientX/clientY)
    tip.style.zIndex = '2147483647';           // sopra tutto
    tip.style.display = 'none';
    tip.style.pointerEvents = 'none';          // il tooltip non cattura click
    tip.style.background = theme.bg;
    tip.style.color = theme.text;
    tip.style.border = `1px solid ${theme.border}`;
    tip.style.borderRadius = '6px';
    tip.style.padding = '8px 10px';
    tip.style.boxShadow = theme.shadow;
    tip.style.font = '12px/1.5 Segoe UI, system-ui, -apple-system, sans-serif';
    tip.style.opacity = '1';
    tip.style.whiteSpace = 'normal';
    tip.style.maxWidth = '360px';
    tip.style.width = 'max-content';
    tip.style.height = 'auto';

    document.body.appendChild(tip);
    ref.tooltip.el = tip;
    return tip;
  }

  // Posizionamento affidabile: forza layout, poi misura e clampa nei bordi del container
  function showTooltip(clientX, clientY, html) {
    const tip = ensureTooltipElement();

    // 1) Inserisci il contenuto e rendi visibile (ma in modo che si possa misurare)
    tip.innerHTML = html;
    tip.style.display = 'block';
    tip.style.visibility = 'hidden';   // evita flicker mentre posizioniamo
    // Posizionamento provvisorio: lasciamo in (0,0) per avere misure corrette
    tip.style.left = '0px';
    tip.style.top  = '0px';

    // 2) Forza reflow e misura in modo affidabile (offsetWidth/Height)
    let w = tip.offsetWidth;
    let h = tip.offsetHeight;
    if (!w || !h) {
      // Fallback in casi estremi
      w = tip.scrollWidth || 240;
      h = tip.scrollHeight || 48;
    }

    // 3) Coordinate viewport (clientX/clientY) + clamp ai bordi della finestra
    const padX = 8, padY = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const maxLeft = vw - w - 8;
    const maxTop  = vh - h - 8;

    const left = Math.max(8, Math.min(clientX + padX, maxLeft));
    const top  = Math.max(8, Math.min(clientY + padY, maxTop));

    // 4) Applica posizione e rendi visibile
    tip.style.left = `${left}px`;
    tip.style.top  = `${top}px`;
    tip.style.visibility = 'visible';

    // Debug (rimuovi quando OK)
    //console.debug('[Tooltip] fixed pos:', { left, top, w, h, vw, vh });
  }

  function hideTooltip() {
    if (ref.tooltip.el) {
      ref.tooltip.el.style.display = 'none';
    }
  }


  // Aggiorna indice giorno → eventi
  function rebuildDayIndex(allEvents) {
    const map = new Map();
    for (const ev of allEvents) {
      const dayBucket = Math.floor(ev.time / 86400) * 86400;
      if (!map.has(dayBucket)) map.set(dayBucket, []);
      map.get(dayBucket).push(ev);
    }
    ref.tooltip.dayIndex = map;
  }

  // Subscribe click su drawings
  function subscribeDrawingClickTooltip() {
    //console.debug('[Tooltip] subscribeDrawingClickTooltip CALLED');

    // Stato per la logica di click
    ref.tooltip.clickedDrawingLast = false;
    let clickToken = 0;

    // Coordinate catturate dal WIDGET (molto più affidabili)
    let lastPoint = { x: null, y: null };

    // 1) Registra coordinate dal widget
    widget.subscribe('mouse_down', (e) => {
      // e.clientX / e.clientY: coordinate viewport (come nel document)
      lastPoint = { x: Number(e?.clientX ?? null), y: Number(e?.clientY ?? null) };
      // azzera flag a inizio ciclo click
      ref.tooltip.clickedDrawingLast = false;
    });

    widget.subscribe('mouse_up', (e) => {
      // aggiorna coordinate anche su mouse_up
      lastPoint = { x: Number(e?.clientX ?? lastPoint.x), y: Number(e?.clientY ?? lastPoint.y) };

      // token per questo click-cycle
      const myToken = ++clickToken;

      // Chiudi solo se NON è arrivato un drawing_event nello stesso tick
      setTimeout(() => {
        if (clickToken === myToken && !ref.tooltip.clickedDrawingLast) {
          hideTooltip(); // 1 solo click in area “vuota”
        }
        // reset per prossimo ciclo
        ref.tooltip.clickedDrawingLast = false;
      }, 0);
    });

    // 2) Registra coordinate anche a livello documento (fallback)
    document.addEventListener('click', (e) => {
      // usiamo solo come backup, il widget di solito fornisce le stesse coords
      if (lastPoint.x == null || lastPoint.y == null) {
        lastPoint = { x: Number(e.clientX), y: Number(e.clientY) };
      }
    });

    // 3) APRI tooltip su click dei drawings (eventi del widget)
    widget.subscribe('drawing_event', (sourceId, eventType) => {
      const isClick = eventType === 'click' || eventType === 'clicked';
      if (!isClick) return;

      // Questo click ha colpito un drawing → annulla eventuale chiusura pianificata
      ref.tooltip.clickedDrawingLast = true;
      clickToken = 0; // invalidiamo token di chiusura attivi

      // Risolvi giorno del drawing
      const dayBucket = ref.idToDayBucket.get(sourceId);
      if (!Number.isFinite(dayBucket)) { hideTooltip(); return; }

      const evs = ref.tooltip.dayIndex.get(dayBucket) || [];
      if (!evs.length) { hideTooltip(); return; }

      const lines = evs
        .filter(e => e && e.planet && typeof e.signIdx === 'number')
        .sort((a,b) => String(a.planet).localeCompare(String(b.planet)))
        .map(formatIngressLine);

      const html = `
        <div><strong>${formatDayUTC(dayBucket)}</strong></div>
        <div>${lines.join('<br>')}</div>
      `;

      // Coordinate: preferisci quelle del WIDGET; se mancano, usa quelle del documento; al limite centra
      const x = (lastPoint.x != null) ? lastPoint.x : (window._lastClickEvent?.clientX ?? Math.round(window.innerWidth / 2));
      const y = (lastPoint.y != null) ? lastPoint.y : (window._lastClickEvent?.clientY ?? Math.round(window.innerHeight / 2));

      // Mostra vicino al click
      showTooltip(x, y, html);

      // chiusura automatica dopo 7 secondi       
      clearTimeout(ref.tooltip.autoHideTimer);
      ref.tooltip.autoHideTimer = setTimeout(hideTooltip, 7000);

    });
  }


  // -- FINE tooltip --


  // render serializzato
  async function render(range) {
    // protezione: se per qualche motivo chart.getVisibleRange() restituisce null o { from: null, to: null }, non disegnare
    if (!range || !Number.isFinite(range.from) || !Number.isFinite(range.to)) {
      console.warn('[Ingressi] Range non valido, skip render:', range);
      return;
    }

    ref.lock = Promise.resolve(ref.lock).then(async () => {
      
      // quando il range è invalid (es. durante un cambio di timeframe o zoom fortissimo), non disegnare
      if (!range || !Number.isFinite(range.from) || !Number.isFinite(range.to)) {
        console.warn('[Ingressi] render: range non valido (skip)', range);
        return;
      }

      await clearIngressDrawings(chart, ref);

      // Raccogli tutti gli ingressi nel range
      const all = [];
      for (const p of ref.planets) {
        all.push(...computeIngressEventsForPlanet(rows, p, range.from, range.to));
      }
      all.sort((a, b) => a.time - b.time);

      // se in zoom-out estremo hai migliaia di ingressi, si imposta un tetto (es. 800) oltre il quale non si disegna evitando code di rendering lunghissime:
      const MAX_DRAW = 800;
      if (all.length > MAX_DRAW) {
        console.warn(`[Ingressi] troppi eventi (${all.length}), limito a ${MAX_DRAW}`);
        all.length = MAX_DRAW; // tronca
      }

      // aggiorna indice day -> eventi
      rebuildDayIndex(all);

      // Raggruppa per timestamp
      const groupsMap = groupEventsByDay(all);
      rebuildDayIndex(all); // <<< serve al tooltip per sapere cosa mostrare
      
      //console.debug('Groups:', [...groupsMap.entries()]);

      // Disegna gruppo per gruppo
      for (const [time, evs] of groupsMap.entries()) {
        if (evs.length === 1) {
          // Caso normale: disegna come facevi già (linea + label/balloon)
          await drawIngresses(chart, evs, ref); // se la tua drawIngresses accetta array, altrimenti passa evs[0]
        } else {
          // Caso collisione: stack verticale
          await drawCollisionStack(chart, evs, ref);
        }
      }
    });
    return ref.lock;
  }



  // subscriptions
  function subscribeRange() {
    if (ref.sub) return;
    const sub = chart.onVisibleRangeChanged();
    const handler = (r) => { render(r); };   // serializzato dal lock
    sub.subscribe(ref, handler);
    ref.sub = { sub, handler };
  }

  function unsubscribeRange() {
    if (!ref.sub) return;
    ref.sub.sub.unsubscribeAll(ref);
    ref.sub = null;
  }

  // public API
  async function show() {
    if (ref.active) return;
    ref.active = true;
    await render(chart.getVisibleRange());
    subscribeRange();
    subscribeDrawingClickTooltip();   // <<< tooltip on
  }


  async function hide() {
    if (!ref.active) return;
    ref.active = false;

    // 1) disiscrivi dal range per fermare nuovi render
    unsubscribeRange(); // usa sub.unsubscribeAll(ref)
    // 2) aspetta qualsiasi render in coda
    await Promise.resolve(ref.lock);
    // 3) pulisci con rimozione "sempre"

    //console.debug('[Ingressi] Prima del clear:', ref.ids.size);
    await clearIngressDrawings(chart, ref);
    //console.debug('[Ingressi] Dopo il clear:', ref.ids.size);

    hideTooltip(); // <<< tooltip off
  }



  return {
    show,
    hide,
    toggle: async () => (ref.active ? hide() : show()),
    dispose: async () => { 
      unsubscribeRange(); 
      hideTooltip();
      await Promise.resolve(ref.lock); 
      await clearIngressDrawings(chart, ref); 
      ref.active = false; 
    },
    isActive: () => ref.active,

    // <<< aggiorna i pianeti nello stato e ridisegna se attivo
    setPlanets: async (list) => {
      ref.planets = new Set(list);
      if (ref.active) {
        await render(chart.getVisibleRange());
      }
    },


    setLineLabelStyle: async ({ enabled, fontsize } = {}) => {
      if (typeof enabled === 'boolean') ref.lineLabel.enabled = enabled;
      if (Number.isFinite(fontsize)) ref.lineLabel.fontsize = fontsize;
      if (ref.active) await render(widget.activeChart().getVisibleRange());
    },

    getDisplayMode: () => ref.displayMode,
    setDisplayMode: async (mode) => {
      const m = (mode === 'line') ? 'line' : 'line+balloon';
      if (m !== ref.displayMode) {
        ref.displayMode = m;
        if (ref.active) await render(widget.activeChart().getVisibleRange());
      }
    },
    
    setPlanetColors: async (colorsMap) => {
      // merge shallow e validazione base
      Object.keys(colorsMap || {}).forEach(k => {
        if (PLANET_COLOR.hasOwnProperty(k)) {
          const v = String(colorsMap[k] || '').trim();
          if (v) PLANET_COLOR[k] = v;
        }
      });
      // se attivo, ridisegna con i nuovi colori
      if (ref.active) await render(widget.activeChart().getVisibleRange());
    },
    getPlanetColors: () => ({ ...PLANET_COLOR }),


  };

}
